<template>
  <div id="case-app" class="border border-solid d-theme-border-grey-light rounded relative overflow-hidden body-module-height z-20">

    <!-- SEARCH BAR -->
    <div class="flex justify-between border d-theme-dark-bg items-center h-16">
<!--      <feather-icon class="md:inline-flex lg:hidden ml-4 mr-4 cursor-pointer" icon="MenuIcon"/>-->
      <vs-input icon-no-border icon="icon-search" size="large" icon-pack="feather" :placeholder="searchPayload.searchQuery ? '' : $t('fields.search')"
                v-model="searchPayload.searchQuery" @focus="toggleFilter()" @keypress.enter="updateSearch"
                class="vs-input-no-border vs-input-no-shdow-focus w-full" ref="searchInput"/>

      <feather-icon class="ml-4 mr-4 cursor-pointer" icon="FilterIcon" @click="toggleFilter()" v-if="!showFilter"/>
      <feather-icon class="ml-4 mr-4 cursor-pointer" icon="XIcon" @click="toggleFilter(false)" v-else/>
    </div>

    <!-- PAGINATION -->
    <div class="h-10">
      <vs-pagination :total="numberOfPages" :max="5" v-model="currentx" class="z-20" />
    </div>

    <template v-show="loaded">
      <div class="hidden md:flex w-full items-center py-1 h-8" v-if="!showFilter">
        <div class="w-1/12 flex items-center justify-center" @click="orderBy('id', 'int')" ref="sortId">
          ID
          <feather-icon icon="ChevronUpIcon" class="cursor-pointer mx-1"
                        svg-classes="h-5 w-5 cursor-pointer hover:bg-primary hover:text-white rounded" v-if="sortDesc.id"/>

          <feather-icon icon="ChevronDownIcon" class="cursor-pointer mx-1"
                        svg-classes="h-5 w-5 cursor-pointer hover:bg-primary hover:text-white rounded" v-else/>
        </div>

        <div class="w-1/12 flex items-center justify-center" @click="orderBy('caseStatus', 'string')">
          Status
          <feather-icon icon="ChevronUpIcon" class="cursor-pointer mx-1"
                        svg-classes="h-5 w-5 cursor-pointer hover:bg-primary hover:text-white rounded" v-if="sortDesc.caseStatus"/>

          <feather-icon icon="ChevronDownIcon" class="cursor-pointer mx-1"
                        svg-classes="h-5 w-5 cursor-pointer hover:bg-primary hover:text-white rounded" v-else/>
        </div>

        <div class="w-10/12 flex flex-row">
          <div class="w-1/5 flex justify-center" @click="orderBy('id')"> Requester </div>
          <div class="w-1/5 flex justify-center" @click="orderBy('id')"> Patient </div>
          <div class="w-1/5 flex justify-center" @click="orderBy('id')"> Start Date </div>
          <div class="w-1/5 flex justify-center" @click="orderBy('id')"> End Date </div>
        </div>
      </div>

      <bds-scroll-area component-class="content-scroll-area specific-height">
        <!-- FILTER -->
        <div class="w-full p-2 bg-gray-300" v-if="showFilter">
          <div class="w-full flex flex-row space-x-2">
            <div class="w-1/3 flex">
              <vs-input v-model="searchPayload.patientName" :placeholder="$t('pages.archive.searchPatient')" class="w-full"/>
            </div>
            <div class="w-1/3">
              <flat-pickr v-model="searchPayload.startDate" :config="{ dateFormat: 'd F Y', maxDate: 'today' }"
                          class="w-full" :placeholder="$t('pages.archive.searchStartDate')"/>
            </div>
            <div class="w-1/3">
              <flat-pickr v-model="searchPayload.endDate" :config="{ dateFormat: 'd F Y', maxDate: 'today' }"
                          class="w-full" :placeholder="$t('pages.archive.searchEndDate')"/>
            </div>
          </div>

          <div class="w-full flex flex-row py-1 space-x-2">
            <div class="w-1/3 flex">
              <choose-category v-model="searchPayload.category" :placeholder="$t('pages.archive.searchCategory')" class="bg-white"/>
            </div>
            <div class="w-1/3 flex">
              <choose-product v-model="searchPayload.product" :placeholder="$t('pages.archive.searchProduct')" class="bg-white"/>
            </div>
            <div class="w-1/3 flex">
              <v-select :options="currencyChoices" label="text" v-model="searchPayload.currency" :reduce="currency => currency.value"
                        :clearable="true" class="w-full bg-white" :placeholder="$t('pages.archive.searchCurrency')">

                <template #selected-option="{ text, icon }">
                  <div class="flex mb-base">
                    <i class="mr-2 flag-icon" :class="icon"/>
                    <label>{{ text }}</label>
                  </div>
                </template>

                <template #option="option">
                  <span class="flag-icon" :class="option.icon"></span>
                  {{ option.text }}
                </template>
              </v-select>
            </div>
          </div>

          <div class="w-full flex justify-end py-2 space-x-2">
            <vs-button type="border" color="warning" icon-pack="feather" icon="icon-x" size="small" @click="resetSearch"
                       class="flex justify-center md:text-xs lg:text-base">
              {{ $t('buttons.reset') }}
            </vs-button>

            <vs-button color="primary" icon-pack="feather" icon="icon-search" size="small" @click="updateSearch"
                       class="flex justify-center md:text-xs lg:text-base">
              {{ $t('buttons.search') }}
            </vs-button>
          </div>
        </div>
        <transition-group name="list-enter-up" class="email__mails" tag="ul" appear>
          <li class="cursor-pointer email__mail-item" v-for="(caseItem, index) in cases" :key="caseItem.id"
              :style="{transitionDelay: (index * 0.1) + 's'}" @click="updateOpenCase(caseItem.id)">

            <case-historical v-model="cases[index]" @removeArchived="removeArchived"/>
          </li>
        </transition-group>
      </bds-scroll-area>
    </template>

    <!-- CASE VIEW SIDEBAR -->
    <case-view
      parent-div="historic-app"
      :case-id="openCaseId"
      :is-case-sidebar-active="isCaseSidebarActive"
      :key="openCaseId"

      @closeSidebar="closeCaseViewSidebar" margin-sidebar="case-sidebar history-sidebar">
    </case-view>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import CaseService from "@/services/Case.service";
import { Case } from "@/models/Case.model";
import {showErrorNotify, showSuccessNotify, sortInt, sortString} from "@/helpers/functionsHelpers";
import CaseView from "@/views/cases/CaseView";
import ChooseCategory from "@/views/components/ChooseCategory";
import ChooseProduct from "@/views/components/ChooseProduct";
import {getCurrencyChoices} from "@/helpers/lists";
import CaseHistorical from "@/views/cases/CaseHistorical";

export default {
  name: "archive",
  components: {
    CaseHistorical,
    ChooseProduct,
    ChooseCategory,
    flatPickr,
    CaseView
  },
  data() {
    return {
      openCaseId: 0,
      isCaseSidebarActive: false,

      sortDesc: {
        id: false,
        caseStatus: false
      },

      queried_cases: [],
      searchPayload: {},

      showFilter: false,
      loaded: false,
      currentx: 1,

      totalCases: 0,
      casesPerPage: 20,
    }
  },
  watch: {
    currentx(newPage, oldPage) {
      if (newPage != oldPage) {
        this.requestQuery(newPage);
      }
    }
  },
  computed: {
    cases() {
      return this.queried_cases;
    },
    numberOfPages() {
      return this.casesPerPage ? Math.ceil(this.totalCases / this.casesPerPage) : 1;
    },
    currencyChoices() {
      return getCurrencyChoices();
    }
  },
  methods: {
    getNewSearchPayload() {
      return {
        searchQuery: null,
        patientName: null,
        startDate: null,
        endDate: null,
        user: 0,
        category: 0,
        product: 0,
        currency: null,
      };
    },
    closeCaseViewSidebar() {
      this.isCaseSidebarActive = false;
    },
    updateOpenCase(caseId) {
      this.openCaseId = caseId;
      this.isCaseSidebarActive = true;
    },
    requestQuery(page = 1) {
      this.loading();
      return CaseService.queryCase(this.searchPayload, page).then((response) => {
        this.totalCases = response.totalCases;
        this.casesPerPage = response.casesPerPage || 20;
        this.queried_cases.splice(0);
        for (let c of response.cases) {
          this.queried_cases.push(Case.FromObj(c));
        }

        this.currentx = page;
        return true;
      }).catch((error) => {
        showErrorNotify(this, error);
        return Promise.reject(error);
      }).finally(() => {
        this.loading(false);
      });
    },
    updateSearch() {
      this.lodash.debounce((payload, vm) => {
        if (!payload) {
          vm.loading(false);
          return;
        }

        vm.requestQuery(1);
      }, 500)(this.searchPayload, this);
    },
    resetSearch() {
      this.searchPayload = this.getNewSearchPayload();
      this.requestQuery(1).then(() => {
        this.toggleFilter(false);
      }, (error) => {
        showErrorNotify(this, error);
      });
    },
    toggleFilter(open = true) {
      this.showFilter = open;
    },

    orderBy(property, type) {
      this.queried_cases.sort((a, b) => {
        switch (type) {
          case 'string': {
            return sortString(a[property], b[property]);
          }
          case 'int': {
            return sortInt(a[property], b[property]);
          }
          case null: {
            console.log('ERROR');
          }
        }
      });

      this.sortDesc[property] = !this.sortDesc[property];
      if (this.sortDesc[property]) {
        this.queried_cases.reverse();
      }
    },
    removeArchived(caseId) {
      CaseService.archiveCase(caseId).then((response) => {
        this.$store.dispatch('Case/setCase', response).then(() => {
          showSuccessNotify(this, this.$t('pages.archive.successfulyUnArchived'));
        });
      }, error => {
        showErrorNotify(this, error);
      });
    },
    loading(open = true) {
      if (open) {
        this.$vs.loading({
          container: this.$refs.searchInput.$el,
          scale: 0.45
        });
      } else {
        this.$vs.loading.close(this.$refs.searchInput.$el);
      }
    },
  },
  created() {
    this.loaded = false;
  },
  mounted() {
    this.searchPayload = this.getNewSearchPayload();
    this.requestQuery(this.currentx).then(() => {
      this.loaded = true;
    });
  }
}
</script>

<style lang="scss">
  @import 'src/assets/scss/cases.scss';
  #case-app {
    .content-scroll-area.specific-height {
      height: calc(100% - 4rem - 2.5rem);
    }

    @media screen and (min-width: $screen-md-min) {
      .content-scroll-area.specific-height {
        height: calc(100% - 4rem - 2rem - 2.5rem);
      }
    }
  }

  .vs-pagination--li span {
    z-index: 21 !important;
  }

  .vs-pagination--li .effect {
    z-index: 20 !important;
  }

  .vs-pagination--buttons {
    z-index: 20 !important;
  }
</style>
